/* eslint-disable */
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Rnd } from 'react-rnd';
import { ref, set, get } from 'firebase/database';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { database, storage } from '../firebase';
import '../styles/VisionBoard.css';
import { auth } from '../firebase';

const VisionBoard = ({ user, userGoal }) => {
  const [images, setImages] = useState({});
  const [scale, setScale] = useState(1);
  const [isResizing, setIsResizing] = useState(false);
  const [containerSize, setContainerSize] = useState({ width: 1000, height: 600 });
  const [canvasSize, setCanvasSize] = useState('large'); // small, medium, large, custom
  const [customWidth, setCustomWidth] = useState(1200);
  const [customHeight, setCustomHeight] = useState(720);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [positions, setPositions] = useState({});
  const [isLocked, setIsLocked] = useState(false);
  const [centerPadding, setCenterPadding] = useState(0); // Padding around center rectangle
  const [localImages, setLocalImages] = useState({}); // Track local images that haven't been uploaded yet
  const containerRef = useRef(null);

  // Canvas size presets
  const sizePresets = {
    small: { width: 800, height: 480 },
    medium: { width: 1000, height: 600 },
    large: { width: 1200, height: 720 },
    custom: { width: customWidth, height: customHeight }
  };

  // Get current canvas dimensions
  const currentCanvasSize = sizePresets[canvasSize];

  // Responsive sizing based on container - optimized for landscape
  const baseScaleRatio = Math.min(currentCanvasSize.width / 1000, currentCanvasSize.height / 600);
  
  const baseRectWidth = 180 * baseScaleRatio;       
  const baseRectHeight = 120 * baseScaleRatio;      
  const baseCenterWidth = 360 * baseScaleRatio;     
  const baseCenterHeight = 240 * baseScaleRatio;    
  const canvasWidth = currentCanvasSize.width;
  const canvasHeight = currentCanvasSize.height;

  // Update container size when canvas size changes
  useEffect(() => {
    setContainerSize(currentCanvasSize);
  }, [canvasSize, customWidth, customHeight]);

  // Load saved vision board on mount
  useEffect(() => {
    console.log('VisionBoard useEffect triggered:', { 
      hasUser: !!user, 
      userUid: user?.uid,
      hasUserGoal: !!userGoal,
      goalTitle: userGoal?.title 
    });
    if (user) {
      loadVisionBoard();
    }
  }, [user]);

  const handleImageUpload = async (key) => {
    // Check if user is authenticated
    if (!user || !user.uid) {
      console.error('User not authenticated for image upload');
      alert('Please sign in to upload images');
      return;
    }

    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*,image/gif';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      // Validate file size (max 20MB)
      if (file.size > 20 * 1024 * 1024) {
        alert('File size must be less than 20MB');
        return;
      }

      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        alert('Please select a valid image file (JPEG, PNG, GIF, or WebP)');
        return;
      }

      try {
        // Store image locally as data URL (no upload yet)
        const reader = new FileReader();
        reader.onload = (ev) => {
          if (ev.target.result && typeof ev.target.result === 'string') {
            // Update local state with data URL
            setImages(prev => ({
              ...prev,
              [key]: ev.target.result
            }));
            // Mark this image as local/pending upload
            setLocalImages(prev => ({
              ...prev,
              [key]: {
                file: file,
                dataUrl: ev.target.result,
                originalName: file.name
              }
            }));
            console.log('Image stored locally, will upload on save');
          }
        };
        reader.onerror = (error) => {
          console.error('Error reading file:', error);
          alert('Failed to read the selected image file.');
        };
        reader.readAsDataURL(file);
      } catch (error) {
        console.error('Error handling image upload:', error);
        alert('Failed to process the selected image.');
      }
    };
    input.click();
  };

  const getScaledPosition = (row, col, isCenter = false) => {
    const scaledRectWidth = baseRectWidth * scale;
    const scaledRectHeight = baseRectHeight * scale;
    const scaledCenterWidth = baseCenterWidth * scale;
    const scaledCenterHeight = baseCenterHeight * scale;
    
    // Use saved position if available
    const elementKey = isCenter ? 'center' : `rect-${row}`;
    if (positions[elementKey]) {
      return {
        x: positions[elementKey].x * (canvasWidth / 1000), // Scale to current canvas
        y: positions[elementKey].y * (canvasHeight / 600),
        width: positions[elementKey].width * baseScaleRatio,
        height: positions[elementKey].height * baseScaleRatio
      };
    }
    
    if (isCenter) {
      // Center rectangle positioned in exact center
      return { 
        x: (canvasWidth - scaledCenterWidth) / 2, 
        y: (canvasHeight - scaledCenterHeight) / 2, 
        width: scaledCenterWidth, 
        height: scaledCenterHeight 
      };
    }
    
    // Calculate center rectangle position with padding
    const centerX = canvasWidth / 2;
    const centerY = canvasHeight / 2;
    const rectLeft = centerX - scaledCenterWidth / 2;
    const rectRight = centerX + scaledCenterWidth / 2;
    const rectTop = centerY - scaledCenterHeight / 2;
    const rectBottom = centerY + scaledCenterHeight / 2;
    
    // Apply padding around center rectangle
    const paddingOffset = centerPadding * baseScaleRatio;
    
    // Perfect Frame Template: 12 rectangles forming uniform border around center with padding
    // All rectangles maintain their size and move outward uniformly with padding
    const borderPositions = [
      // Top row (4 rectangles) - maintains horizontal alignment, moves up by padding
      { x: rectLeft - scaledRectWidth, y: rectTop - scaledRectHeight - paddingOffset },     // 0: top-left corner
      { x: rectLeft, y: rectTop - scaledRectHeight - paddingOffset },                       // 1: top-left-center  
      { x: rectLeft + scaledRectWidth, y: rectTop - scaledRectHeight - paddingOffset },     // 2: top-right-center
      { x: rectRight, y: rectTop - scaledRectHeight - paddingOffset },                      // 3: top-right corner
      
      // Left side (2 rectangles) - moves left by padding, maintains vertical alignment
      { x: rectLeft - scaledRectWidth - paddingOffset, y: rectTop },                        // 4: left-top
      { x: rectLeft - scaledRectWidth - paddingOffset, y: rectTop + scaledRectHeight },     // 5: left-bottom
      
      // Right side (2 rectangles) - moves right by padding, maintains vertical alignment  
      { x: rectRight + paddingOffset, y: rectTop },                                         // 6: right-top
      { x: rectRight + paddingOffset, y: rectTop + scaledRectHeight },                      // 7: right-bottom
      
      // Bottom row (4 rectangles) - maintains horizontal alignment, moves down by padding
      { x: rectLeft - scaledRectWidth, y: rectBottom + paddingOffset },                     // 8: bottom-left corner
      { x: rectLeft, y: rectBottom + paddingOffset },                                       // 9: bottom-left-center
      { x: rectLeft + scaledRectWidth, y: rectBottom + paddingOffset },                     // 10: bottom-right-center  
      { x: rectRight, y: rectBottom + paddingOffset }                                       // 11: bottom-right corner
    ];
    
    const pos = borderPositions[row] || borderPositions[0];
    
    return { 
      x: pos.x, 
      y: pos.y, 
      width: scaledRectWidth, 
      height: scaledRectHeight 
    };
  };

  const handleResize = useCallback((newWidth, newHeight, isCenter = false) => {
    setIsResizing(true);
    const baseReference = isCenter ? baseCenterWidth : baseRectWidth;
    const newScale = Math.max(0.3, Math.min(1.5, newWidth / baseReference));
    setScale(newScale);
  }, [baseCenterWidth, baseRectWidth]);

  const handleResizeStop = useCallback(() => {
    setIsResizing(false);
    const snappedScale = Math.round(scale * 4) / 4;
    setScale(snappedScale);
  }, [scale]);

  const handleDragStop = useCallback((key, d) => {
    // Save position relative to canvas size for responsiveness
    setPositions(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        x: (d.x / canvasWidth) * 1000, // Normalize to 1000px base
        y: (d.y / canvasHeight) * 600   // Normalize to 600px base
      }
    }));
  }, [canvasWidth, canvasHeight]);

  const handleElementResizeStop = useCallback((key, ref, position) => {
    // Save both position and size
    setPositions(prev => ({
      ...prev,
      [key]: {
        x: (position.x / canvasWidth) * 1000,
        y: (position.y / canvasHeight) * 600,
        width: parseInt(ref.style.width, 10) / baseScaleRatio,
        height: parseInt(ref.style.height, 10) / baseScaleRatio
      }
    }));
  }, [canvasWidth, canvasHeight, baseScaleRatio]);

  const saveVisionBoard = async () => {
    if (!user) return;
    
    setIsSaving(true);
    try {
      // First, upload all local images to Firebase Storage
      const uploadedImages = { ...images };
      
      for (const [key, localImageData] of Object.entries(localImages)) {
        if (localImageData && localImageData.file) {
          try {
            // Create storage reference with proper path
            const sanitizedKey = key.replace(/[^a-zA-Z0-9-_]/g, '_');
            const fileName = `${sanitizedKey}_${Date.now()}_${localImageData.originalName.replace(/[^a-zA-Z0-9.-]/g, '_')}`;
            const imageRef = storageRef(storage, `visionBoard/${user.uid}/${fileName}`);
            
            // Upload with metadata
            const metadata = {
              contentType: localImageData.file.type,
              customMetadata: {
                uploadedBy: user.uid,
                uploadedAt: new Date().toISOString(),
                originalName: localImageData.originalName
              }
            };

            await uploadBytes(imageRef, localImageData.file, metadata);
            const downloadURL = await getDownloadURL(imageRef);
            
            // Replace local data URL with Firebase download URL
            uploadedImages[key] = downloadURL;
            console.log(`Image ${key} uploaded successfully:`, fileName);
          } catch (uploadError) {
            console.error(`Error uploading image ${key}:`, uploadError);
            // Keep the local data URL as fallback
            uploadedImages[key] = localImageData.dataUrl;
          }
        }
      }

      const visionBoardData = {
        images: uploadedImages,
        positions,
        canvasSize,
        customWidth,
        customHeight,
        scale,
        centerPadding,
        isLocked: true,
        updatedAt: Date.now()
      };

      await set(ref(database, `visionBoards/${user.uid}`), visionBoardData);
      
      // Update local state with uploaded URLs
      setImages(uploadedImages);
      // Clear local images since they're now uploaded
      setLocalImages({});
      setIsLocked(true);
      console.log('Vision board saved and locked successfully!');
    } catch (error) {
      console.error('Error saving vision board:', error);
      alert('Failed to save vision board. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const loadVisionBoard = async () => {
    if (!user) {
      console.log('VisionBoard: No user available for loading');
      return;
    }
    
    console.log('VisionBoard: Starting to load vision board for user:', user.uid);
    setIsLoading(true);
    try {
      const snapshot = await get(ref(database, `visionBoards/${user.uid}`));
      if (snapshot.exists()) {
        const data = snapshot.val();
        console.log('VisionBoard: Loaded data from Firebase:', data);
        setImages(data.images || {});
        setPositions(data.positions || {});
        setCanvasSize(data.canvasSize || 'large');
        setCustomWidth(data.customWidth || 1200);
        setCustomHeight(data.customHeight || 720);
        setScale(data.scale || 1);
        setCenterPadding(data.centerPadding || 0);
        setIsLocked(data.isLocked || false);
        console.log('VisionBoard: All data loaded successfully!');
      } else {
        console.log('VisionBoard: No existing vision board found, using defaults');
      }
    } catch (error) {
      console.error('VisionBoard: Error loading vision board:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const unlockVisionBoard = () => {
    setIsLocked(false);
  };

  const resetToTemplate = () => {
    setPositions({});
    setScale(1);
    setCenterPadding(0);
  };

  const renderElements = () => {
    const elements = [];
    
    // Render center rectangle (largest)
    const centerLayout = getScaledPosition(0, 0, true);
    elements.push(
      <Rnd
        key="center"
        size={{ width: centerLayout.width, height: centerLayout.height }}
        position={{ x: centerLayout.x, y: centerLayout.y }}
        minWidth={baseCenterWidth * 0.3}
        minHeight={baseCenterHeight * 0.3}
        maxWidth={baseCenterWidth * 1.5}
        maxHeight={baseCenterHeight * 1.5}
        bounds="parent"
        disableDragging={isLocked}
        enableResizing={!isLocked}
        onDragStop={(e, d) => !isLocked && handleDragStop('center', d)}
        onResize={(e, direction, ref) => {
          if (!isLocked) handleResize(parseInt(ref.style.width, 10), parseInt(ref.style.height, 10), true);
        }}
        onResizeStop={(e, direction, ref, delta, position) => {
          if (!isLocked) {
            handleResizeStop();
            handleElementResizeStop('center', ref, position);
          }
        }}
        className={`vision-center-rect ${isLocked ? 'locked' : ''}`}
        style={{ 
          zIndex: isResizing ? 10 : 5,
          cursor: isLocked ? 'default' : 'move'
        }}
      >
        <div
          style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => !isResizing && !isLocked && handleImageUpload('center')}
        >
          {images.center && typeof images.center === 'string' && (images.center.startsWith('data:image') || images.center.startsWith('http')) ? (
            <img src={images.center} alt="Vision Center" className="vision-board-img-rect" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            <img 
              src="/default-vision-placeholder.png" 
              alt="Upload Main Vision" 
              className="vision-board-img-rect default-placeholder" 
              onError={(e) => {
                e.target.style.display = 'none';
                console.warn('Failed to load default vision placeholder');
              }}
              style={{ 
                width: '100%', 
                height: '100%', 
                objectFit: 'cover'
              }} 
            />
          )}
        </div>
      </Rnd>
    );

    // Render 12 border rectangles around the center (complete frame)
    for (let i = 0; i < 12; i++) {
      const key = `rect-${i}`;
      const rectLayout = getScaledPosition(i, 0);
      
      elements.push(
        <Rnd
          key={key}
          size={{ width: rectLayout.width, height: rectLayout.height }}
          position={{ x: rectLayout.x, y: rectLayout.y }}
          minWidth={baseRectWidth * 0.3}
          minHeight={baseRectHeight * 0.3}
          maxWidth={baseRectWidth * 1.5}
          maxHeight={baseRectHeight * 1.5}
          bounds="parent"
          disableDragging={isLocked}
          enableResizing={!isLocked}
          onDragStop={(e, d) => !isLocked && handleDragStop(key, d)}
          onResize={(e, direction, ref) => {
            if (!isLocked) handleResize(parseInt(ref.style.width, 10), parseInt(ref.style.height, 10), false);
          }}
          onResizeStop={(e, direction, ref, delta, position) => {
            if (!isLocked) {
              handleResizeStop();
              handleElementResizeStop(key, ref, position);
            }
          }}
          className={`vision-square ${isLocked ? 'locked' : ''}`}
          style={{ 
            zIndex: isResizing ? 10 : 2,
            cursor: isLocked ? 'default' : 'move'
          }}
        >
          <div
            style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            onClick={() => !isResizing && !isLocked && handleImageUpload(key)}
          >
            {images[key] ? (
              <img src={images[key]} alt={`Vision ${key}`} className="vision-board-img" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <img 
                src="/default-vision-placeholder.png" 
                alt={`Upload Vision ${key}`} 
                className="vision-board-img default-placeholder" 
                onError={(e) => {
                  e.target.style.display = 'none';
                  console.warn('Failed to load default vision placeholder');
                }}
                style={{ 
                  width: '100%', 
                  height: '100%', 
                  objectFit: 'cover'
                }} 
              />
            )}
          </div>
        </Rnd>
      );
    }

    return elements;
  };

      // Show loading state
      if (isLoading) {
        return (
          <div className="vision-board-container">
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '400px',
              color: 'white'
            }}>
              <div className="spinner" style={{
                width: '40px',
                height: '40px',
                border: '4px solid rgba(255,255,255,0.1)',
                borderTop: '4px solid #007AFF',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                marginBottom: '1rem'
              }}></div>
              <p>Loading your Vision Board...</p>
            </div>
          </div>
        );
      }

      return (
    <div className="vision-board-container">
      {/* Debug Info - Temporary */}
      {!isLocked && (
        <div style={{
          position: 'fixed',
          top: '10px',
          right: '10px',
          background: 'rgba(0,0,0,0.8)',
          color: 'white',
          padding: '0.5rem',
          borderRadius: '8px',
          fontSize: '0.8rem',
          zIndex: 1000
        }}>
          <div>User: {user ? '✅ Authenticated' : '❌ Not logged in'}</div>
          <div>Goal: {userGoal?.title || 'No goal set'}</div>
          <div>Images: {Object.keys(images).length} total ({Object.keys(localImages).length} pending upload)</div>
          <div>Canvas: {canvasWidth}×{canvasHeight}</div>
          <div>Loading: {isLoading ? 'Yes' : 'No'}</div>
        </div>
      )}
      
      {/* Controls - Only show when unlocked */}
      {!isLocked && (
        <div className="vision-board-controls" style={{
          display: 'flex',
          gap: '1rem',
          marginBottom: '1rem',
          padding: '1rem',
          background: 'rgba(255,255,255,0.1)',
          borderRadius: '12px',
          alignItems: 'center',
          flexWrap: 'wrap'
        }}>
        {/* Lock Status */}
        {isLocked && (
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '0.5rem',
            padding: '0.5rem 1rem',
            background: 'rgba(255,193,7,0.2)',
            borderRadius: '6px',
            border: '1px solid rgba(255,193,7,0.4)'
          }}>
            <span style={{ color: '#ffc107', fontSize: '1rem' }}>🔒</span>
            <span style={{ color: '#ffc107', fontSize: '0.9rem', fontWeight: '500' }}>
              Frame Locked
            </span>
          </div>
        )}

        {/* Size Selector */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <label style={{ color: '#fff', fontSize: '0.9rem', fontWeight: '500' }}>Canvas Size:</label>
          <select 
            value={canvasSize} 
            onChange={(e) => !isLocked && setCanvasSize(e.target.value)}
            disabled={isLocked}
            style={{
              padding: '0.5rem',
              borderRadius: '6px',
              border: '1px solid rgba(255,255,255,0.2)',
              background: isLocked ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.1)',
              color: isLocked ? '#999' : '#fff',
              fontSize: '0.9rem',
              cursor: isLocked ? 'not-allowed' : 'pointer'
            }}
          >
            <option value="small">Small (800×480)</option>
            <option value="medium">Medium (1000×600)</option>
            <option value="large">Large (1200×720)</option>
            <option value="custom">Custom</option>
          </select>
        </div>

        {/* Center Padding Control */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <label style={{ color: '#fff', fontSize: '0.9rem', fontWeight: '500' }}>Center Padding:</label>
          <input
            type="range"
            min="0"
            max="100"
            value={centerPadding}
            onChange={(e) => !isLocked && setCenterPadding(parseInt(e.target.value))}
            disabled={isLocked}
            style={{
              width: '100px',
              cursor: isLocked ? 'not-allowed' : 'pointer'
            }}
          />
          <span style={{ 
            color: isLocked ? '#999' : '#fff', 
            fontSize: '0.8rem', 
            minWidth: '35px',
            textAlign: 'center'
          }}>
            {centerPadding}px
          </span>
        </div>

        {/* Custom Size Inputs */}
        {canvasSize === 'custom' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input
              type="number"
              value={customWidth}
              onChange={(e) => !isLocked && setCustomWidth(parseInt(e.target.value))}
              disabled={isLocked}
              placeholder="Width"
              style={{
                width: '80px',
                padding: '0.5rem',
                borderRadius: '6px',
                border: '1px solid rgba(255,255,255,0.2)',
                background: isLocked ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.1)',
                color: isLocked ? '#999' : '#fff',
                fontSize: '0.9rem',
                cursor: isLocked ? 'not-allowed' : 'text'
              }}
            />
            <span style={{ color: '#fff' }}>×</span>
            <input
              type="number"
              value={customHeight}
              onChange={(e) => !isLocked && setCustomHeight(parseInt(e.target.value))}
              disabled={isLocked}
              placeholder="Height"
              style={{
                width: '80px',
                padding: '0.5rem',
                borderRadius: '6px',
                border: '1px solid rgba(255,255,255,0.2)',
                background: isLocked ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.1)',
                color: isLocked ? '#999' : '#fff',
                fontSize: '0.9rem',
                cursor: isLocked ? 'not-allowed' : 'text'
              }}
            />
          </div>
        )}

        {/* Save/Load/Unlock Buttons */}
        <div style={{ display: 'flex', gap: '0.5rem', marginLeft: 'auto' }}>
          <button
            onClick={resetToTemplate}
            style={{
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              border: 'none',
              background: 'rgba(255,255,255,0.2)',
              color: '#fff',
              fontSize: '0.9rem',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            📐 Perfect Frame
          </button>
          <button
            onClick={saveVisionBoard}
            disabled={!user || isSaving || isLocked}
            style={{
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              border: 'none',
              background: (isSaving || isLocked) ? 'rgba(255,255,255,0.1)' : 'rgba(0,122,255,0.8)',
              color: '#fff',
              fontSize: '0.9rem',
              fontWeight: '500',
              cursor: (isSaving || isLocked) ? 'not-allowed' : 'pointer',
              opacity: (isSaving || isLocked) ? 0.6 : 1
            }}
          >
            {isSaving ? 'Saving...' : isLocked ? '🔒 Locked' : Object.keys(localImages).length > 0 ? `💾 Save & Lock (${Object.keys(localImages).length} pending)` : '💾 Save & Lock'}
          </button>
          <button
            onClick={loadVisionBoard}
            disabled={!user || isLoading}
            style={{
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              border: 'none',
              background: isLoading ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.2)',
              color: '#fff',
              fontSize: '0.9rem',
              fontWeight: '500',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              opacity: isLoading ? 0.6 : 1
            }}
          >
            {isLoading ? 'Loading...' : '📂 Load'}
          </button>
          {isLocked && (
            <button
              onClick={unlockVisionBoard}
              style={{
                padding: '0.5rem 1rem',
                borderRadius: '6px',
                border: 'none',
                background: 'rgba(255,193,7,0.8)',
                color: '#fff',
                fontSize: '0.9rem',
                fontWeight: '500',
                cursor: 'pointer'
              }}
            >
              🔓 Unlock
            </button>
          )}
        </div>
              </div>
      )}

      {/* Locked Frame Controls - Minimal unlock button */}
      {isLocked && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          marginBottom: '1rem'
        }}>
          <button
            onClick={unlockVisionBoard}
            style={{
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              border: 'none',
              background: 'rgba(255,193,7,0.8)',
              color: '#fff',
              fontSize: '0.9rem',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            🔓 Edit Vision Board
          </button>
        </div>
      )}

      {/* Canvas */}
      <div 
        ref={containerRef}
        className="vision-board-canvas" 
        style={{ 
          position: 'relative', 
          width: canvasWidth,
          height: canvasHeight,
          margin: '0 auto', 
          background: isLocked ? 'transparent' : 'rgba(255,255,255,0.1)', 
          borderRadius: isLocked ? 0 : 16,
          overflow: 'hidden',
          border: isLocked ? 'none' : '2px solid rgba(255,255,255,0.1)'
        }}
      >
        {!isLocked && (
          <div style={{ 
            position: 'absolute', 
            top: 10, 
            left: 10, 
            color: '#666', 
            fontSize: Math.max(10, 12 * baseScaleRatio) + 'px',
            background: 'rgba(255,255,255,0.8)',
            padding: '4px 8px',
            borderRadius: '4px',
            zIndex: 100
          }}>
            Scale: {Math.round(scale * 100)}% | {canvasWidth}×{canvasHeight}
          </div>
        )}
        {renderElements()}
      </div>
    </div>
  );
};

export default VisionBoard; 